import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ImageIcon,
  Plus,
  Trash2,
  Sparkles,
  Upload,
  Loader2,
  Check,
  Layers,
} from "lucide-react";

const MODULE_OPTIONS = [
  { value: "bang_ke_tien_mat::", label: "Bảng kê tiền mặt", target_module: "bang_ke_tien_mat", sheet_type: null },
  { value: "bang_ke_doanh_thu::", label: "Doanh thu Pippy", target_module: "bang_ke_doanh_thu", sheet_type: null },
  { value: "doanh_thu_ngay::", label: "Doanh thu ngày", target_module: "doanh_thu_ngay", sheet_type: null },
  { value: "bao_cao_kd::", label: "Báo cáo kinh doanh", target_module: "bao_cao_kd", sheet_type: null },
  { value: "so_thu_chi::so_quy", label: "Sổ thu chi — Sổ quỹ", target_module: "so_thu_chi", sheet_type: "so_quy" },
  { value: "so_thu_chi::chung_tu", label: "Sổ thu chi — Chứng từ đối chiếu", target_module: "so_thu_chi", sheet_type: "chung_tu" },
];

const MODULE_LABEL = {
  bang_ke_tien_mat: "Bảng kê tiền mặt",
  bang_ke_doanh_thu: "Doanh thu Pippy",
  doanh_thu_ngay: "Doanh thu ngày",
  bao_cao_kd: "Báo cáo kinh doanh",
  so_thu_chi: "Sổ thu chi",
};
const SHEET_LABEL = {
  so_quy: "Sổ quỹ",
  chung_tu: "Chứng từ đối chiếu",
};

function moduleLabel(target_module, sheet_type) {
  let s = MODULE_LABEL[target_module] || target_module;
  if (sheet_type) s += ` — ${SHEET_LABEL[sheet_type] || sheet_type}`;
  return s;
}

export default function SamplesTab() {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [maxPerModule, setMaxPerModule] = useState(5);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadModule, setUploadModule] = useState("bang_ke_tien_mat::");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchSamples = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/discord/samples");
      setGroups(data.groups || []);
      setTotal(data.total || 0);
      setMaxPerModule(data.max_per_module || 5);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tải mẫu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSamples();
  }, [fetchSamples]);

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error("Vui lòng chọn file ảnh");
      return;
    }
    const opt = MODULE_OPTIONS.find((o) => o.value === uploadModule);
    if (!opt) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("target_module", opt.target_module);
      if (opt.sheet_type) fd.append("sheet_type", opt.sheet_type);
      await api.post("/discord/samples", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      toast.success("Đã thêm mẫu! AI đã trích xuất cấu trúc.");
      setShowUpload(false);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchSamples();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tải lên");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (sampleId, fileName) => {
    if (!window.confirm(`Xoá mẫu "${fileName}"?`)) return;
    try {
      await api.delete(`/discord/samples/${sampleId}`);
      toast.success("Đã xoá mẫu");
      fetchSamples();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xoá");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-500" data-testid="samples-loading">
        Đang tải mẫu…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="samples-tab">
      {/* Header */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5 space-y-3">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-base font-bold text-[#F8FAFC] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              Mẫu ảnh AI ({total} mẫu)
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Upload 2-5 ảnh mẫu cho mỗi tab Kế toán. AI sẽ đọc <strong className="text-emerald-400">cấu trúc cột</strong> (column headers, key phrases, layout)
              của từng mẫu, lưu thành "vân tay". Khi có ảnh mới đến từ Discord, AI so sánh với mẫu để tự động phân loại — kể cả ảnh tên là <code>image.png</code>.
            </p>
          </div>
          <Button
            onClick={() => setShowUpload(true)}
            className="bg-emerald-500 hover:bg-emerald-400 text-white h-8 text-xs"
            data-testid="samples-upload-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Thêm mẫu
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 text-[10px] text-slate-400">
          <Badge className="bg-slate-700/40 text-slate-300 border-0">
            Threshold tự import: ≥ 0.75
          </Badge>
          <Badge className="bg-slate-700/40 text-slate-300 border-0">Max {maxPerModule} mẫu/tab</Badge>
          <Badge className="bg-slate-700/40 text-slate-300 border-0">Cache theo image hash 30 ngày</Badge>
        </div>
      </div>

      {/* Empty state */}
      {groups.length === 0 ? (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-10 text-center" data-testid="samples-empty">
          <ImageIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Chưa có mẫu ảnh nào. Bấm "Thêm mẫu" để tải lên ảnh điển hình cho từng tab.</p>
          <p className="text-xs text-slate-600 mt-2">
            Gợi ý: 1 ảnh "Bảng kê tiền mặt" + 1 ảnh "Bảng kê doanh thu" + 1 ảnh "Báo cáo kinh doanh" + 1 ảnh "Sổ quỹ" + 1 ảnh "Chứng từ chuyển khoản" là tối thiểu.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => {
            const groupKey = `${g.target_module}::${g.sheet_type || ""}`;
            return (
              <div
                key={groupKey}
                className="bg-[#1e293b] border border-[#334155] rounded-xl p-4"
                data-testid={`samples-group-${g.target_module}-${g.sheet_type || "none"}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-indigo-400" />
                    <h4 className="text-sm font-semibold text-slate-200">
                      {moduleLabel(g.target_module, g.sheet_type)}
                    </h4>
                    <Badge
                      className={`${
                        g.samples.length >= maxPerModule ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"
                      } border-0 text-[10px]`}
                    >
                      {g.samples.length}/{maxPerModule} mẫu
                    </Badge>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {g.samples.map((s) => (
                    <div
                      key={s.id}
                      className="bg-[#0f172a] border border-[#334155] rounded-lg overflow-hidden flex flex-col"
                      data-testid={`sample-card-${s.id}`}
                    >
                      <div className="relative bg-slate-900 aspect-video flex items-center justify-center overflow-hidden">
                        {s.thumbnail_b64 ? (
                          <img
                            src={`data:image/jpeg;base64,${s.thumbnail_b64}`}
                            alt={s.file_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-slate-700" />
                        )}
                        <button
                          onClick={() => handleDelete(s.id, s.file_name)}
                          className="absolute top-1 right-1 p-1 rounded bg-black/50 hover:bg-red-500/70 text-white"
                          data-testid={`sample-delete-${s.id}`}
                          title="Xoá mẫu"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="p-2 space-y-1.5">
                        <p className="text-[11px] text-slate-300 font-medium truncate" title={s.file_name}>
                          {s.file_name}
                        </p>
                        {s.fingerprint_summary?.layout_type && (
                          <Badge className="bg-slate-700/40 text-slate-400 border-0 text-[9px]">
                            {s.fingerprint_summary.layout_type}
                          </Badge>
                        )}
                        {s.fingerprint_summary?.column_headers && s.fingerprint_summary.column_headers.length > 0 && (
                          <p className="text-[9px] text-slate-500 truncate" title={s.fingerprint_summary.column_headers.join(", ")}>
                            <span className="text-slate-600">Cột: </span>
                            {s.fingerprint_summary.column_headers.slice(0, 4).join(", ")}
                            {s.fingerprint_summary.column_headers.length > 4 ? "…" : ""}
                          </p>
                        )}
                        <p className="text-[9px] text-slate-600 flex items-center gap-1">
                          <Check className="w-2.5 h-2.5" /> {s.created_by_name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="samples-upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] text-base">Thêm mẫu ảnh AI</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Tải lên 1 ảnh điển hình. AI sẽ đọc cấu trúc cột/dòng và lưu làm "vân tay" cho tab này.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">Module đích</label>
              <Select value={uploadModule} onValueChange={setUploadModule}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200 h-9 text-xs" data-testid="samples-upload-module">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  {MODULE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-slate-300 text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">File ảnh (PNG/JPG/PDF, max 10MB)</label>
              <div
                className="border-2 border-dashed border-[#334155] hover:border-emerald-500/50 rounded-lg p-4 text-center cursor-pointer transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="samples-upload-dropzone"
              >
                {uploadFile ? (
                  <>
                    <Check className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-300 truncate">{uploadFile.name}</p>
                    <p className="text-[10px] text-slate-500 mt-1">
                      {(uploadFile.size / 1024).toFixed(1)} KB
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-slate-500 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">Bấm để chọn ảnh từ máy</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && f.size > 10 * 1024 * 1024) {
                      toast.error("File quá lớn (>10MB)");
                      return;
                    }
                    setUploadFile(f || null);
                  }}
                  data-testid="samples-upload-file-input"
                />
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-[10px] text-amber-300">
              ⚠️ Quá trình AI trích cấu trúc có thể mất 5-15 giây và tiêu tốn token Emergent LLM Key.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowUpload(false);
                setUploadFile(null);
              }}
              className="border-[#334155] text-slate-300 hover:bg-[#334155] h-8 text-xs"
              disabled={uploading}
            >
              Huỷ
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || uploading}
              className="bg-emerald-500 hover:bg-emerald-400 text-white h-8 text-xs"
              data-testid="samples-upload-submit"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> AI đang đọc cấu trúc…
                </>
              ) : (
                <>
                  <Upload className="w-3.5 h-3.5 mr-1" /> Tải lên + Phân tích AI
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
